/* eslint-disable jsx-a11y/label-has-for */
import React, { useCallback, useMemo, useState } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import Pane from 'components/Common/Pane'
import { mediaQueryUp } from 'components/utils/breakpoint'

import type { AdultLabel } from 'lib/offer/occupancyUtils'
import { getMaxOccupancies } from 'lib/offer/occupancyUtils'
import OccupancyItemActions from './OccupancyItemActions'
import OccupancyChildAgeSelect from './OccupancyChildAgeSelect'
import { only2AAllowedForLanson } from 'lib/offer/offerSpecificChangesUtils'
import Counter from '../../../../LuxKit/Counter'
import BodyText from '../../../../LuxKit/Typography/BodyText'
import clsx from 'clsx'
import Group from 'components/utils/Group'
import { countWhere } from 'lib/array/arrayUtils'

const OccupantsContainer = styled(Pane)`
  background-color: transparent;
  flex: 1;
  border-radius: ${props => props.theme.borderRadius.S};
  box-shadow: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    border-radius: 0;
    border: 0;
    padding: ${rem(16)} ${rem(24)};

    &.leftBorder {
      border-left: 1px solid ${props => props.theme.palette.neutral.default.five};
    }

    &.smallView {
      border-left: none;
      padding: ${rem(16)} 0 0;
    }
  }
`

const OccupantsTypesContainer = styled.div`
  flex-grow: 1;
  flex-direction: column;
  display: flex;
  flex-wrap: wrap;
  column-gap: ${rem(40)};
  row-gap: ${rem(24)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
  }

  &.vertical-mode {
    flex-direction: column;
  }
`

const OccupantsCountsContainer = styled.div`
  flex-direction: column;
  display: flex;
  flex-wrap: wrap;
  column-gap: ${rem(40)};
  row-gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
  }
`

const AgeType = styled(Group)`
  flex-grow: 1;
`

const OccupantIncrementType = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${mediaQueryUp.tablet} {
    display: inline-block;

    & + & {
      margin-top: 0;
    }

    > * + * {
      margin-top: ${rem(4)};
    }
  }
`

const OccupantsIncrementer = styled(Counter)`
  display: inline-flex;

  ${mediaQueryUp.tablet} {
    margin-top: ${rem(12)};
  }
`

const ItemWrapper = styled(Pane)`
  box-shadow: none;
  background-color: transparent;
  padding: 0 ${rem(20)} ${rem(24)};

  ${mediaQueryUp.tablet} {
    display: flex;
    padding: 0;
    border: 1px solid ${props => props.theme.palette.neutral.default.five};
    border-radius: ${props => props.theme.borderRadius.S};

    &.smallView {
      display: grid;
      border: none;
      border-top: 1px solid ${props => props.theme.palette.neutral.default.five};

      &:first-child {
        border-top: none;
      }
    }
  }
`

const TitleSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${rem(16)} 0;

  ${mediaQueryUp.tablet} {
    align-items: flex-start;
    padding: ${rem(16)};

    &:not(.smallView) {
      min-width: ${rem(130)};
      justify-content: center;
    }

    &.smallView {
      padding: ${rem(16)} 0 0;
      align-items: center;
    }
  }
`

const MobileActions = styled(OccupancyItemActions)`
  ${mediaQueryUp.tablet} {
    display: none;
    &.smallView {
      display: flex;
    }
  }
`

const DesktopActions = styled(OccupancyItemActions)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    margin-left: auto;
    justify-self: flex-end;
    align-self: start;
    &.smallView {
      display: none;
    }
  }
`

const DEFAULT_OCCUPANCY: App.Occupants = {
  adults: 1,
  infants: 0,
  children: 0,
  teenagers: 0,
  childrenAge: [],
  childrenBirthDate: [],
}

export type OccupancyChildVariant = 'none' | 'children-only' | 'date-of-birth' | 'child-age' | 'infants'

interface Props extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'onReset'> {
  title?: string
  /* occupancy objects don't have an ID so we need to provide one */
  itemId: number
  occupancy: App.Occupants
  name?: string
  maxChildAge?: number
  maxInfantAge?: number
  minInfantAge?: number
  maxTeenagerAge?: number
  capacities?: Array<App.RoomCapacity>
  onChange: (itemId: number, nextOccupancy: App.Occupants, eventLabel?: string) => void
  onRemove?: (itemId: number) => void
  onReset?: (itemId: number) => void
  showRemove?: boolean
  childrenAgeLabel?: string
  smallView?: boolean
  adultLabelType?: AdultLabel
  childVariant?: OccupancyChildVariant
  /** If this is false, the reset button will only appear when the control is dirty */
  showResetWhenClean?: boolean
  offerType?: App.OfferType
  offerId?: string
  departureDate?: string
  removeAdultAgeCaption?: boolean
  isAddingExtraGuests?: boolean
  currentRoomOccupancy?: App.Occupants
  /** Disable all "+" buttons to prevent adding more occupants */
  disableAddOccupants?: boolean
}

function OccupancyItem(props: Props) {
  const {
    maxChildAge = 17,
    maxInfantAge = -1,
    minInfantAge = 0,
    maxTeenagerAge = -1,
    title,
    itemId,
    occupancy,
    onChange,
    onReset,
    onRemove,
    name,
    capacities,
    showRemove,
    childrenAgeLabel = 'Age at time of check-in',
    smallView = false,
    adultLabelType = 'adult',
    childVariant = 'child-age',
    showResetWhenClean = false,
    offerType,
    offerId,
    departureDate,
    removeAdultAgeCaption = false,
    isAddingExtraGuests,
    currentRoomOccupancy = DEFAULT_OCCUPANCY,
    disableAddOccupants = false,
    ...rest
  } = props

  const {
    adults,
    infants = 0,
    children = 0,
    childrenAge = [],
    childrenBirthDate = [],
  } = occupancy

  const childrenRequireDateOfBirth = childVariant === 'date-of-birth'
  const infantVariant = childVariant === 'infants'
  const [isDirty, setDirty] = useState<boolean>(false)

  const restrictCapacitiesForOffer = offerId && only2AAllowedForLanson(offerId)

  const maxCapacities = useMemo(
    () => getMaxOccupancies(capacities, occupancy, { maxChildAge, maxInfantAge, maxTeenagerAge }),
    [capacities, occupancy, maxChildAge, maxInfantAge, maxTeenagerAge],
  )

  const { maxAdults, minAdults, minChildren } = useMemo(() => {
    const calculatedMaxAdults = restrictCapacitiesForOffer ? 2 : maxCapacities.adults
    const calculatedMinAdults = restrictCapacitiesForOffer ? 2 : 1

    if (isAddingExtraGuests) {
      return {
        maxAdults: calculatedMaxAdults,
        minAdults: currentRoomOccupancy.adults,
        minChildren: (currentRoomOccupancy.teenagers ?? 0) + (currentRoomOccupancy.children ?? 0) + (currentRoomOccupancy.infants ?? 0),
      }
    }

    return { maxAdults: calculatedMaxAdults, minAdults: calculatedMinAdults, minChildren: 0 }
  }, [restrictCapacitiesForOffer, maxCapacities.adults, isAddingExtraGuests, currentRoomOccupancy.adults, currentRoomOccupancy.teenagers, currentRoomOccupancy.children, currentRoomOccupancy.infants])

  const onAdultChange = useCallback((val: number) => {
    setDirty(true)
    const newOccupancies = { ...occupancy, adults: val }
    onChange(itemId, newOccupancies, val > occupancy.adults ? 'add-adult' : undefined)
  }, [occupancy, onChange, itemId])

  const onChildrenChange = useCallback((val: number) => {
    setDirty(true)

    // For infant variant, directly update children count
    if (infantVariant) {
      const newOccupancies = {
        ...occupancy,
        children: val,
      }
      onChange(itemId, newOccupancies, val > (occupancy.children ?? 0) ? 'add-kid' : undefined)
      return
    }

    const nextChildrenAges = [...childrenAge]
    const nextChildrenBirthDate = [...childrenBirthDate]
    const isIncrementing = val > nextChildrenAges.length

    if (isIncrementing) {
      nextChildrenAges.push(-1)
      if (childrenRequireDateOfBirth) nextChildrenBirthDate.push(undefined)
    } else {
      nextChildrenAges.pop()
      if (childrenRequireDateOfBirth) nextChildrenBirthDate.pop()
    }

    const teenagers = countWhere(nextChildrenAges, (age) => age > maxChildAge && age <= maxTeenagerAge)
    const infants = countWhere(nextChildrenAges, (age) => age >= minInfantAge && age <= maxInfantAge)
    const children = nextChildrenAges.length - teenagers - infants

    const newOccupancies = {
      ...occupancy,
      teenagers,
      children,
      infants,
      childrenAge: nextChildrenAges,
      childrenBirthDate: nextChildrenBirthDate,
    }

    onChange(itemId, newOccupancies, isIncrementing ? 'add-kid' : undefined)
  }, [childrenAge, onChange, itemId, occupancy, minInfantAge, maxInfantAge, maxChildAge, maxTeenagerAge, childrenBirthDate, childrenRequireDateOfBirth, infantVariant])

  const onChildrenAgeChange = useCallback((childId: number, age: number, birthDate?: string) => {
    setDirty(true)
    const nextChildrenAges = [...childrenAge]
    nextChildrenAges[childId] = age

    const teenagers = countWhere(nextChildrenAges, (age) => age > maxChildAge && age <= maxTeenagerAge)
    const infants = countWhere(nextChildrenAges, (age) => age >= minInfantAge && age <= maxInfantAge)
    const children = nextChildrenAges.length - teenagers - infants

    const nextChildrenBirthDate = [...childrenBirthDate]
    if (childrenRequireDateOfBirth) {
      nextChildrenBirthDate[childId] = birthDate
    }

    const newOccupancy = {
      ...occupancy,
      children,
      teenagers,
      infants,
      childrenAge: nextChildrenAges,
      childrenBirthDate: nextChildrenBirthDate,
    }
    onChange(itemId, newOccupancy)
  }, [childrenAge, occupancy, onChange, itemId, minInfantAge, maxInfantAge, maxChildAge, maxTeenagerAge, childrenBirthDate, childrenRequireDateOfBirth])

  const onResetClick = useCallback(() => {
    setDirty(false)
    onReset?.(itemId)
  }, [onReset, itemId])

  const onRemoveClick = useCallback(() => {
    onRemove?.(itemId)
  }, [onRemove, itemId])

  const onInfantsChange = useCallback((val: number) => {
    setDirty(true)
    const isIncrementing = val > (infants || 0)

    const newOccupancies = {
      ...occupancy,
      infants: val,
    }

    onChange(itemId, newOccupancies, isIncrementing ? 'add-kid' : undefined)
  }, [infants, itemId, occupancy, onChange])

  const onlyInfantCanBeAdded = maxCapacities.children === 0 && maxChildAge === 0 && (maxCapacities.infants ?? 0) > 0
  const minAdultAge = (onlyInfantCanBeAdded ? maxInfantAge : Math.max(maxTeenagerAge, maxChildAge)) + 1

  const intl = useIntl()
  const adultLabel = useMemo(() => {
    return adultLabelType === 'adult'
      ? intl.formatMessage({
          id: 'rMBsb+bhqw',
          defaultMessage: 'Adults',
          description: 'Label for adults in occupancy picker in the occupancy or guest picker',
        })
      : intl.formatMessage({
          id: 'P1pNQ5h1cP',
          defaultMessage: 'Travellers',
          description: 'Label for travellers in occupancy picker in the traveller form',
        })
  }, [adultLabelType, intl])

  const childLabel = useMemo(() => {
    return infantVariant
      ? intl.formatMessage({
          id: 'PK/eCBi9jq',
          defaultMessage: 'Children',
          description: 'Label for children in occupancy picker in the occupancy or guest picker',
        })
      : intl.formatMessage({
          id: 'sp0IeoKDD5',
          defaultMessage: 'Child',
          description: 'Label for child in occupancy picker in the occupancy or guest picker',
        })
  }, [infantVariant, intl])

  const childrenValue = infantVariant ? children : Math.max(childrenAge.length, children)
  const childrenMaxCapacity = infantVariant
    ? maxCapacities.children
    : (maxCapacities.teenagers ?? 0) + (maxCapacities.children ?? 0) + (maxCapacities.infants ?? 0)
  const childrenMax = disableAddOccupants ? childrenValue : childrenMaxCapacity

  const infantsMaxCapacity = maxCapacities.infants ?? 0
  const infantsMax = disableAddOccupants ? infants : infantsMaxCapacity

  return (
    <ItemWrapper className={clsx('occupancy-picker-item', { smallView })} data-testid={`occupancy-item-${itemId}`} {...rest}>
      {title &&
        <TitleSection className={clsx({ smallView })}>
          <BodyText variant="medium" weight="bold" format="titlecase">{title}</BodyText>
          <MobileActions
            className={clsx({ smallView })}
            showReset={isDirty && !!onReset}
            showRemove={showRemove}
            onReset={onResetClick}
            onRemove={onRemoveClick}
          />
        </TitleSection>}
      <OccupantsContainer className={clsx('occupancy-picker-item', { smallView, leftBorder: !!title })}>
        <OccupantsTypesContainer className={clsx({ 'vertical-mode': childrenRequireDateOfBirth })}>
          <OccupantsCountsContainer>
            <OccupantIncrementType>
              <label>
                <BodyText variant="medium">{adultLabel}</BodyText>
                {!removeAdultAgeCaption &&
                  <BodyText variant="medium" colour="neutral-three">
                    {intl.formatMessage(
                      {
                        id: 'tESiVuYUcr',
                        defaultMessage: 'Ages {age} and above',
                        description: 'Age requirement label for adults in occupancy picker',
                      },
                      { age: minAdultAge },
                    )}
                  </BodyText>}
              </label>
              <OccupantsIncrementer
                data-testid={`occupancy-item-${itemId}-adults`}
                value={adults}
                onChange={onAdultChange}
                max={disableAddOccupants ? adults : maxAdults}
                min={minAdults}
                name={`${name}.adults`}
              />
            </OccupantIncrementType>
            {!restrictCapacitiesForOffer && childVariant !== 'none' && <OccupantIncrementType>
              <label>
                <BodyText variant="medium">{childLabel}</BodyText>
                <BodyText variant="medium" colour="neutral-three">
                  {intl.formatMessage(
                    {
                      id: 'NiW6F/lRoF',
                      defaultMessage: 'Up to {age} years old',
                      description: 'Age requirement label for children in occupancy picker',
                    },
                    { age: minAdultAge },
                  )}
                </BodyText>
              </label>
              <OccupantsIncrementer
                data-testid={`occupancy-item-${itemId}-children`}
                value={childrenValue}
                onChange={onChildrenChange}
                max={childrenMax}
                min={minChildren}
                name={`${name}.children`}
              />
            </OccupantIncrementType>}
            {infantVariant && <OccupantIncrementType>
              <label>
                <BodyText variant="medium">
                  {intl.formatMessage({
                    id: 'hy6zHnvXPB',
                    defaultMessage: 'Infants',
                    description: 'Label for infants in occupancy picker in the occupancy or guest picker',
                  })}
                </BodyText>
                <BodyText variant="medium" colour="neutral-three">
                  {intl.formatMessage({
                    id: 'eVroK4jmHY',
                    defaultMessage: 'Under 2 years old',
                    description: 'Age requirement label for infants in occupancy picker',
                  })}
                </BodyText>
              </label>
              <OccupantsIncrementer
                data-testid={`occupancy-item-${itemId}-infant`}
                value={infants}
                onChange={onInfantsChange}
                max={infantsMax}
                min={0}
                name={`${name}.infants`}
              />
            </OccupantIncrementType>}
          </OccupantsCountsContainer>
          {childVariant !== 'children-only' && childrenAge.length > 0 &&
            <AgeType direction="vertical" tabletDirection="horizontal" gap={24} wrap="wrap">
              {childrenAge.map((child, index) => <OccupancyChildAgeSelect
                key={index}
                capacities={capacities}
                occupancy={occupancy}
                maxChildAge={maxChildAge}
                maxInfantAge={maxInfantAge}
                minInfantAge={minInfantAge}
                maxTeenagerAge={maxTeenagerAge}
                childId={index}
                defaultValue={child === -1 ? undefined : child}
                onChange={onChildrenAgeChange}
                data-testid={`occupancy-item-${itemId}-childrenAge-${index}`}
                name={`${name}.childrenAge[${index}]::number`}
                childrenRequireDateOfBirth={childrenRequireDateOfBirth}
                departureDate={departureDate}
                childrenAgeLabel={childrenAgeLabel}
                required
              />)}
            </AgeType>}
        </OccupantsTypesContainer>
        <DesktopActions
          className={clsx({ smallView })}
          showReset={(isDirty || showResetWhenClean) && !!onReset}
          showRemove={showRemove}
          onReset={onResetClick}
          onRemove={onRemoveClick}
        />
      </OccupantsContainer>
    </ItemWrapper>
  )
}

export default React.memo(OccupancyItem)
